import { TGetCompanyDto } from "@/api/services/main/company/dtos/get-company.dto";
import { TStudyLinkStatus } from "@/api/services/main/rso/dtos/get-rso.dto";
import {
  cancelCompanyStudyLinkRequestApi,
  createCompanyStudyLinkRequestApi,
  syncCompanyVisaInformationApi,
  unlinkCompanyStudyLinkApi,
  useSyncCompanyStudyLink,
} from "@/api/services/main/company";
import { getVisaStudyAccessApprovalUrl } from "@/api/services/main/rso";
import { TVisaActionType, VisaLinkSection } from "@/components/commons/VisaLinkSection";
import { TVisaLinkSubmitPayload, VisaLinkModal } from "@/components/commons/VisaLinkModal";
import { VisaApprovalUrlModal } from "@/components/commons/VisaApprovalUrlModal";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useDialog } from "@/hooks/useDialog";
import {
  COMPANY_MANAGEMENT_NUMBER_ERROR_MESSAGE,
  COMPANY_MANAGEMENT_NUMBER_REGEX,
} from "@/features/company/constants/management-number";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { FCC } from "@/types/common";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

const UNLINK_VISA_TALENT_COUNT_TEXT = Array(4).fill("-").join("");

// 同期: BE が Visa へ問い合わせて管理人数キャッシュを更新する。解除・申請も実 API を使用する。
const visaApiMap: Record<Exclude<TVisaActionType, "link">, () => Promise<unknown>> = {
  unlink: unlinkCompanyStudyLinkApi,
  sync: syncCompanyVisaInformationApi,
  cancel: cancelCompanyStudyLinkRequestApi,
};

// 連携ステータスの表示ラベル。接続中を最優先し、それ以外は直近申請のステータスで表示する。
const NOT_LINKED_STATUS_LABEL = "連携なし";
const STUDY_LINK_STATUS_LABEL: Record<TStudyLinkStatus, string> = {
  pending: "連携認証待ち",
  approved: "連携中",
  rejected: "連携なし",
  expired: "連携なし",
  cancelled: "連携なし",
};

type TCompanyVisaLinkSectionProps = {
  data: Partial<TGetCompanyDto>;
  isEditMode?: boolean;
};

export const CompanyVisaLinkSection: FCC<TCompanyVisaLinkSectionProps> = ({ data, isEditMode = false }) => {
  const queryClient = useQueryClient();

  const studyLink = data.studyLink;
  const isLinked = !!studyLink?.isConnected;
  // 連携済みは有効連携 (isConnected) のみで判定する。連携解除後は申請ステータスが approved のまま残るため、
  // 未連携時の approved は「連携なし」とみなす (pending/rejected/expired のみ申請ステータスを表示)。
  const statusValue = isLinked
    ? STUDY_LINK_STATUS_LABEL.approved
    : studyLink?.status && studyLink.status !== "approved"
      ? STUDY_LINK_STATUS_LABEL[studyLink.status]
      : NOT_LINKED_STATUS_LABEL;
  // 認証待ちの申請がある場合は、再申請を防ぐため連携セットアップを無効化する。
  const isPendingApproval = !isLinked && studyLink?.status === "pending";
  // 最終同期日は連携日時 (connectedAt) を表示する。
  const lastSyncDate = studyLink?.connectedAt ?? null;

  const [activeAction, setActiveAction] = useState<TVisaActionType | null>(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  // 認証ページの URL。自動でタブを開かず、確認モーダル経由でユーザー操作で開く。
  const [pendingApprovalUrl, setPendingApprovalUrl] = useState<string | null>(null);
  const unlinkConfirm = useDialog();
  const cancelConfirm = useDialog();

  // 認証待ちの間は BE 経由で Visa と状態を同期し続ける。
  // 同期結果が pending 以外 (承認・却下・期限切れ) になったら企業詳細を再取得して UI を更新する。
  const { data: syncedLink } = useSyncCompanyStudyLink(isPendingApproval);
  useEffect(() => {
    if (syncedLink && syncedLink.status !== "pending") {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL] });
    }
  }, [syncedLink, queryClient]);

  // RSO 詳細と同様、セットアップは直接モーダルを開く。
  // 管理番号はモーダルで入力した登録番号が申請成功時に BE 側で保存されるため、事前入力は不要。
  const handleSetupClick = () => {
    setIsLinkModalOpen(true);
  };

  const handleVisaAction = async (type: Exclude<TVisaActionType, "link">) => {
    try {
      setActiveAction(type);
      await visaApiMap[type]();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL] });
      toast.success(API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    } finally {
      setActiveAction(null);
    }
  };

  // 連携解除は不可逆な操作のため、実行前に確認モーダルで同意を得る。
  const handleUnlinkClick = async () => {
    const agree = await unlinkConfirm.show();
    if (!agree) return;
    await handleVisaAction("unlink");
  };

  // 認証待ちの申請取り消しも確認モーダルで同意を得てから実行する。
  const handleCancelClick = async () => {
    const agree = await cancelConfirm.show();
    if (!agree) return;
    await handleVisaAction("cancel");
  };

  // 連携リクエストを送信する。エラー時は再入力できるよう VisaLinkModal 側で再 throw を捕捉する。
  const handleLinkSubmit = async (payload: TVisaLinkSubmitPayload) => {
    try {
      const { data: created } = await createCompanyStudyLinkRequestApi(payload);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL] });
      if (payload.authType === "self") {
        // self: 認証モーダルを表示し、同意操作で認証ページを開く (toast は出さない)。
        const approvalUrl = created?.publicId ? getVisaStudyAccessApprovalUrl(created.publicId) : null;
        if (approvalUrl) setPendingApprovalUrl(approvalUrl);
      } else {
        // delegate: BE が担当者へ認証依頼メールを送る。
        toast.success("担当者へ認証依頼メールを送信しました");
      }
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  return (
    <>
      <VisaLinkSection
        title="irohana visa 連携（いろはな限定機能）"
        description="自社支援のirohana visaアカウントと連携することで、特定技能外国人を学習者として簡単に取り込みや同期が可能になります。"
        statusValue={statusValue}
        lastSyncDate={lastSyncDate}
        visaCount={{
          label: "irohana visaの管理人数",
          // 連携中は Visa から同期したキャッシュ値 (studyLink.visaEmployeeCount) を表示する。RSO 詳細と同じ仕組み。
          value: isLinked ? `${studyLink?.visaEmployeeCount ?? 0}` : UNLINK_VISA_TALENT_COUNT_TEXT,
          tooltip: "特定技能1号および特定活動の内定または在籍人数のみ集計しています。",
        }}
        studyCount={{
          label: "irohana studyの学習者数",
          value: data.currentLearnerCount != null ? `${data.currentLearnerCount}` : undefined,
          tooltip: "受験ステータスが進行中である受講生のみ集計しています。（合格済みや不合格終了となった受講生はカウントしていません）",
        }}
        note="自社支援向けはirohana担当者のみの機能であり、顧客からは使えない・見えない機能であることに注意ください。"
        isLinked={isLinked}
        isEditMode={isEditMode}
        activeAction={activeAction}
        isPendingApproval={isPendingApproval}
        onUnlink={handleUnlinkClick}
        onSync={() => handleVisaAction("sync")}
        onSetup={handleSetupClick}
        onCancelRequest={handleCancelClick}
      />
      <ConfirmDeleteModal
        open={unlinkConfirm.open}
        title="連携解除の確認"
        message="irohana visa との連携を解除してもよろしいですか？"
        onClose={unlinkConfirm.cancel}
        onOk={unlinkConfirm.confirm}
      />
      <ConfirmDeleteModal
        open={cancelConfirm.open}
        title="連携申請の取り消し確認"
        message="irohana visa への連携申請 (認証待ち) を取り消してもよろしいですか？"
        onClose={cancelConfirm.cancel}
        onOk={cancelConfirm.confirm}
      />
      {/* Company の番号は 管理番号 (英大文字5桁+数字5桁、例: GCLMQ71010)。RSO の 登録番号 (YY登-XXXXXX) とは形式が異なる。 */}
      <VisaLinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onOk={handleLinkSubmit}
        licenseNumberLabel="企業ID"
        licenseNumberPattern={COMPANY_MANAGEMENT_NUMBER_REGEX}
        licenseNumberErrorMessage={COMPANY_MANAGEMENT_NUMBER_ERROR_MESSAGE}
        // Company は所属機関の自動取り込みを除外し、契約ラベルも登録支援機関様向けの記載を外す。
        linkBenefits={["特定技能外国人を受験者として自動取り込み"]}
        contractLabel="irohana visa の契約"
      />
      <VisaApprovalUrlModal approvalUrl={pendingApprovalUrl} onClose={() => setPendingApprovalUrl(null)} />
    </>
  );
};
